import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { TermSheetFacility, TermSheetFacilityPainPoint } from '../../../shared/models';
import { PlatformRootUrl } from '../../../shared/utils/platform';

interface TermSheetFacilityActionButtonProps {
	facility: TermSheetFacility;
	onEdit: (facility: TermSheetFacility) => void;
	onFees: (facility: TermSheetFacility) => void;
	onCollateral: (facility: TermSheetFacility) => void;
	onCreateDealSonar: (facility: TermSheetFacility) => void;
	onDelete: (facility: TermSheetFacility) => void;
}

type FacilityActionItem = keyof typeof FacilityActionItemMap;
const FacilityActionItemMap = {
	Edit: 'Edit',
	Fees: 'Fees',
	Collateral: 'Collateral',
	CreateDealSonar: 'Create Deal Sonar Item',
	ViewDealSonar: 'View Deal Sonar Item',
	Delete: 'Delete',
};

export const TermSheetFacilityActionButton: React.FC<TermSheetFacilityActionButtonProps> = ({
	facility,
	onEdit,
	onFees,
	onCollateral,
	onCreateDealSonar,
	onDelete,
}) => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);
	const hasPainPoints = facility.painPoint != null;
	const showCreateDealSonar = !hasPainPoints;
	const showViewDealSonar = hasPainPoints;
	const painPointStatus = getTermSheetFacilityPainPointStatus(facility.painPoint);

	const onClickViewDealSonar = (facility: TermSheetFacility) => {
		window.location.href = `${PlatformRootUrl}/Emp/LoanWatchData?tab=refinance&itemId=${facility.itemIdentifier}&status=${painPointStatus}`;
	};

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClick = (action: FacilityActionItem) => {
		switch (action) {
			case 'Edit':
				onEdit(facility);
				break;
			case 'Fees':
				onFees(facility);
				break;
			case 'Collateral':
				onCollateral(facility);
				break;
			case 'CreateDealSonar':
				onCreateDealSonar(facility);
				break;
			case 'ViewDealSonar':
				onClickViewDealSonar(facility);
				break;
			case 'Delete':
				onDelete(facility);
				break;
		}
	};

	const handleClose = (event: Event) => {
		if (anchorRef.current?.contains(event.target as HTMLElement)) {
			return;
		}

		setOpen(false);
	};

	const handleMenuItemClick = (
		event: React.MouseEvent<HTMLLIElement, MouseEvent>,
		actionItem: FacilityActionItem
	) => {
		setOpen(false);
		handleClick(actionItem);
	};

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef} aria-label="split button">
				<Button onClick={handleToggle}>Actions</Button>
				<Button
					size="small"
					aria-controls={open ? 'split-button-menu' : undefined}
					aria-expanded={open ? 'true' : undefined}
					aria-label="select merge strategy"
					aria-haspopup="menu"
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				open={open}
				style={{ zIndex: 1000, margin: 0 }}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
						}}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id="split-button-menu" autoFocusItem>
									<MenuItem onClick={event => handleMenuItemClick(event, 'Edit')}>
										{FacilityActionItemMap['Edit']}
									</MenuItem>
									{facility.feesProvided && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'Fees')}>
											{FacilityActionItemMap['Fees']}
										</MenuItem>
									)}
									<MenuItem onClick={event => handleMenuItemClick(event, 'Collateral')}>
										{FacilityActionItemMap['Collateral']}
									</MenuItem>
									{showCreateDealSonar && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'CreateDealSonar')}>
											{FacilityActionItemMap['CreateDealSonar']}
										</MenuItem>
									)}
									{showViewDealSonar && (
										<MenuItem onClick={event => handleMenuItemClick(event, 'ViewDealSonar')}>
											{FacilityActionItemMap['ViewDealSonar']}
										</MenuItem>
									)}
									<MenuItem onClick={event => handleMenuItemClick(event, 'Delete')}>
										{FacilityActionItemMap['Delete']}
									</MenuItem>
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};

function getTermSheetFacilityPainPointStatus(painPoint: TermSheetFacilityPainPoint | null): string {
	if (painPoint == null) return '-1';
	return painPoint.isActive ? '1' : '0';
}
